import {
  Building2,
  ChevronsRightIcon,
  ClipboardCopyIcon,
  CogIcon,
  FolderIcon,
  HomeIcon,
  LogOutIcon,
  ScanIcon,
} from 'lucide-react'
import { Link, useLocation, useRouteContext } from '@tanstack/react-router'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../ui/sidebar'
import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import { Suspense, useCallback } from 'react'
import { Skeleton } from '../ui/skeleton'
import { useSuspenseQuery } from '@tanstack/react-query'
import { userMeProfileOptions } from '@/data/queryOptions'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { useDevModeStore } from '@/stores/useDevModeStore'

export type NavigationItem = {
  label: string
  href: string
  hrefWithOrg?: (orgId: string) => string
  icon: React.ReactNode
  enable: () => boolean
}

const navigationItems: NavigationItem[] = [
  {
    label: 'Scan',
    href: '/scan',
    icon: <ScanIcon size={16} />,
    enable: () => true,
  },
  {
    label: 'Home',
    href: '/home',
    hrefWithOrg: (orgId: string) => `/org/${orgId}/home`,
    icon: <HomeIcon size={16} />,
    enable: () => false, // Temporarily hidden
  },
  {
    label: 'Documents',
    href: '/documents',
    hrefWithOrg: (orgId: string) => `/org/${orgId}/documents`,
    icon: <FolderIcon size={16} />,
    enable: () => true,
  },
]

export function LeftNavDashboard() {
  const { state, toggleSidebar } = useSidebar()
  const { pathname } = useLocation()
  const { TENANT_ID } = useIdTokenAndTenantId()

  return (
    <Sidebar collapsible="icon">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <div className="text-copy-secondary flex w-full flex-row items-center justify-between">
                  {state === 'expanded' && (
                    <div className="flex items-center gap-2 pl-3">
                      <img
                        src="/assets/logo.svg"
                        alt="LineWise"
                        className="h-8 w-8"
                      />
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    onClick={toggleSidebar}
                    className="size-12 p-3.5"
                  >
                    <ChevronsRightIcon
                      size={18}
                      className={cn(
                        state === 'expanded' ? 'rotate-180' : '',
                        'transition-transform duration-300',
                      )}
                    />
                  </Button>
                </div>
              </SidebarMenuItem>
            </SidebarMenu>
            <SidebarMenu>
              {navigationItems
                .filter((item) => item.enable())
                .map((item) => {
                  const isActive = pathname.includes(item.href)
                  const href =
                    item.hrefWithOrg && TENANT_ID
                      ? item.hrefWithOrg(TENANT_ID)
                      : item.href
                  return (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.label}
                        isActive={isActive}
                        size="lg"
                        className="text-copy-secondary"
                      >
                        <Link to={href}>
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-2">
        <Suspense fallback={<Skeleton className="h-12 w-full" />}>
          <SidebarUserNavMenu />
        </Suspense>
      </SidebarFooter>
    </Sidebar>
  )
}

function SidebarUserNavMenu() {
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { authState, availableTenants } = useRouteContext({
    from: '/_authenticated',
  })
  const { data: userById } = useSuspenseQuery(
    userMeProfileOptions(TENANT_ID!, idToken!),
  )
  const { devMode } = useDevModeStore()

  const hasMultipleTenants = availableTenants && availableTenants.length > 1

  const handleCopyAuthToken = useCallback(() => {
    navigator.clipboard.writeText(idToken ?? '')
  }, [idToken])

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="flex p-2! group-data-[collapsible=icon]:p-2!"
            >
              <Avatar
                className="focus-visible:border-ring focus-visible:ring-ring/50 shrink-0 focus-visible:ring-[3px]"
                role="button"
                tabIndex={0}
              >
                <AvatarImage src={authState?.photoURL ?? ''} />
                <AvatarFallback>
                  {authState?.displayName?.split(' ').map((name) => name[0]) ??
                    ''}
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-copy-primary line-clamp-1 truncate text-sm font-medium">
                  {userById?.name ?? 'Unknown'}
                </span>
                <span className="text-copy-secondary line-clamp-1 truncate text-xs">
                  {userById?.email ?? 'Unknown'}
                </span>
              </div>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-w-80 min-w-40">
            {devMode && (
              <>
                <DropdownMenuItem>
                  <p className="w-full max-w-40 wrap-anywhere">
                    {authState?.uid}
                  </p>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCopyAuthToken} role="button">
                  <ClipboardCopyIcon />
                  Copy Auth Token
                </DropdownMenuItem>
              </>
            )}
            {hasMultipleTenants && (
              <DropdownMenuItem asChild>
                <Link to="/select-tenant">
                  <Building2 />
                  Switch Organization
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link
                to="/user/$userId"
                params={{ userId: authState?.uid! }}
                className="flex flex-row items-center gap-2 text-nowrap"
              >
                <CogIcon />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild variant="destructive">
              <Link to="/logout">
                <LogOutIcon />
                Log Out
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
